import * as React from "react";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import apiClient from "api";
import { gettext } from "i18n";

export interface DeletableUser {
  id: number;
  email: string;
  display_name: string;
}

/**
 * Deletion is only allowed once the user's email has been typed in, so a
 * misclick can never delete anyone. The backend checks the same email again.
 */
export const isDeletionConfirmed = (
  user: DeletableUser,
  confirmEmail: string,
): boolean =>
  !!user.email &&
  confirmEmail.trim().toLowerCase() === user.email.trim().toLowerCase();

interface DeleteUserModalProps {
  show: boolean;
  user: DeletableUser;
  onHide: () => void;
  onDeleted: () => void;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  show,
  user,
  onHide,
  onDeleted,
}: DeleteUserModalProps) => {
  const [confirmEmail, setConfirmEmail] = React.useState<string>("");
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!show) {
      setConfirmEmail("");
    }
  }, [show]);

  const confirmed = isDeletionConfirmed(user, confirmEmail);

  const onDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmed || submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.request("/admin/delete-user", {
        method: "POST",
        data: { user_id: user.id, confirm_email: confirmEmail },
      });
    } catch (e: any) {
      console.error(e);
      toast.error(
        `${gettext("Failed to delete user!")} ${gettext(
          e.message?.toString() || "Unknown error",
        )}`,
      );
      setSubmitting(false);
      return;
    }
    // Stays submitting: onDeleted navigates away from the deleted user
    toast.success(gettext("User deleted successfully"));
    onDeleted();
  };

  return (
    <Modal
      show={show}
      onHide={() => !submitting && onHide()}
      backdrop="static"
      centered
      aria-labelledby="delete-user-modal-title"
    >
      <Form onSubmit={onDelete}>
        <Modal.Header closeButton>
          <Modal.Title id="delete-user-modal-title">
            {gettext("Delete user")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {gettext(
              "Are you sure you want to permanently delete this user? This action cannot be undone.",
            )}
          </p>
          <p className="fw-bold">
            {user.display_name} ({user.email})
          </p>
          <p>
            {gettext(
              "The user loses access to all organizations and their API tokens and bulk exports are removed. Files, reports and data filters they created are kept.",
            )}
          </p>
          <Form.Group controlId="delete-user-confirm-email">
            <Form.Label>
              {gettext("Type the user's email address to confirm")}
            </Form.Label>
            <Form.Control
              type="text"
              autoComplete="off"
              autoFocus
              value={confirmEmail}
              placeholder={user.email}
              onChange={(e) => setConfirmEmail(e.target.value)}
              disabled={submitting}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={submitting}
          >
            {gettext("Cancel")}
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={!confirmed || submitting}
          >
            {gettext("Delete user")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DeleteUserModal;
